import { useState } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { BASE_URL } from '../Urls';
import { toast } from 'react-toastify';
import '../styles/account-pending-deletion-modal.css';

function patchStoredUser(patch) {
  try {
    const raw = JSON.parse(localStorage.getItem('user') || '{}');
    const next = { ...raw, ...patch };
    localStorage.setItem('user', JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/**
 * Shown after login (or on load) when the account is in the post-deletion-request grace window.
 */
export default function AccountPendingDeletionModal() {
  const { user, dispatch } = useAuthContext();
  const [restoring, setRestoring] = useState(false);

  if (!user?.accountPendingDeletion) {
    return null;
  }

  if (user.accountPendingDeletionAcknowledged) {
    return null;
  }

  const purgeDate = user.accountScheduledPurgeAt
    ? new Date(user.accountScheduledPurgeAt)
    : null;
  const purgeLabel = purgeDate && !Number.isNaN(purgeDate.getTime())
    ? purgeDate.toLocaleDateString(undefined, { dateStyle: 'medium' })
    : 'the scheduled date';

  const handleDismiss = () => {
    patchStoredUser({ accountPendingDeletionAcknowledged: true });
    dispatch({ type: 'UPDATE_USER', payload: { accountPendingDeletionAcknowledged: true } });
  };

  const handleRestore = async () => {
    if (!user?.token || !user?.username) return;
    setRestoring(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/user/${encodeURIComponent(user.username)}/cancel-account-deletion`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({}),
        }
      );
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.error || 'Could not restore account');
        return;
      }
      patchStoredUser({
        accountPendingDeletion: false,
        accountScheduledPurgeAt: null,
        accountPendingDeletionAcknowledged: false,
      });
      dispatch({
        type: 'UPDATE_USER',
        payload: {
          accountPendingDeletion: false,
          accountScheduledPurgeAt: null,
          accountPendingDeletionAcknowledged: false,
        },
      });
      toast.success('Your account has been restored.');
    } catch {
      toast.error('Network error. Try again.');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="account-pending-overlay" role="dialog" aria-modal="true" aria-labelledby="pending-del-title">
      <div className="account-pending-card">
        <h2 id="pending-del-title">Account scheduled for deletion</h2>
        <p>
          Your profile and library are hidden from others. All data will be permanently removed after{' '}
          <strong>{purgeLabel}</strong> unless you restore your account.
        </p>
        <div className="account-pending-actions">
          <button
            type="button"
            className="account-pending-btn account-pending-btn--primary"
            onClick={handleRestore}
            disabled={restoring}
          >
            {restoring ? 'Restoring…' : 'Restore account'}
          </button>
          <button
            type="button"
            className="account-pending-btn account-pending-btn--ghost"
            onClick={handleDismiss}
          >
            I understand
          </button>
        </div>
      </div>
    </div>
  );
}
